/**
 * Where each entry of this tab's history points, so a page's back arrow can go BACK when the page before it is the one
 * it means, instead of opening that page again on top. Opening it again was a loop (Sam, 2026-10-23): festival ->
 * lineup -> back opened the festival anew -> back returned to the lineup.
 *
 * React Router keeps the entry's position in history.state.idx; NavHistoryRecorder (App) writes the path at each one.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const pathAt = new Map();

const currentIdx = () => (typeof window !== 'undefined' ? window.history.state?.idx : undefined);

export function recordNav(idx, path) {
  if (Number.isInteger(idx)) pathAt.set(idx, path);
}

/** The path of the entry before this one, if this app session saw it. */
export function previousPath(idx = currentIdx()) {
  return Number.isInteger(idx) && idx > 0 ? (pathAt.get(idx - 1) ?? null) : null;
}

const pathname = (p) => (p || '').split('?')[0].split('#')[0];

/**
 * Leave this page for `target`: back one step when the previous entry is `target` (same pathname), otherwise put
 * `target` in this page's place (replace), so the back arrow never stacks a page that leads back here.
 */
export function goBackTo(navigate, target, options = {}) {
  const prev = previousPath();
  if (prev && pathname(prev) === pathname(target)) {
    navigate(-1);
    return;
  }
  navigate(target, { ...options, replace: true });
}

export function NavHistoryRecorder() {
  const location = useLocation();
  useEffect(() => {
    recordNav(currentIdx(), `${location.pathname}${location.search}`);
  }, [location]);
  return null;
}
